import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import debounce from "lodash/debounce";

import { questionProps, userShape } from "lib/propTypes";
import { Checkbox } from "common/DS/Checkables";
import { formatPhone } from "lib/util";
import { usePost } from "redux/fetch-hooks";
import { useSelector, useDispatch } from "react-redux";
import { checkinSetNudged } from "redux/checkin/actions";
import TextBubble from "common/DS/TextBubble";
import { feature } from "lib/featureFlags";

import QuestionButtonGroup from "./QuestionButtonGroup";
import Textarea from "./QuestionTextarea";

// after this many characters, consider a previous prefetch to be invalid
const PREFETCH_CHAR_COUNTDOWN = 20;
const FOLLOWUP_MAX_CHARS = 300;
const FOLLOWUP_MIN_CHARS = 12;

const WORD_COUNT_AVG_CHAR_MIN = {
    8: 3,
    6: 6,
    4: 9,
    2: 12,
};
const WORD_COUNT_KEYS = [8, 6, 4, 2];

const wordCountFilter = (comment) => {
    const words = comment.split(" ");
    const filtered = words.filter((word) => word.length > 0);
    const wordCount = filtered.length;
    const avgCharPerWord =
        wordCount > 0 ? filtered.reduce((acc, word) => acc + word.length, 0) / wordCount : 0;
    const minCharPerWord = WORD_COUNT_KEYS.reduce((acc, key) => {
        if (wordCount < key) {
            return WORD_COUNT_AVG_CHAR_MIN[key];
        }
        return acc;
    }, null);
    return !minCharPerWord || avgCharPerWord >= minCharPerWord;
};

const TextFollowUp = (
    {
        follow_up: followUpText,
        id,
        handleBack,
        handleChange,
        handleSubmit,
        handleSkip,
        optional,
        visible,
        isLastQuestion,
        extraData,
        selfIdentifyOff,
        selectedAnswerData,
        selfIdentified,
        toggleAnonymous,
    },
    { t: _ },
) => {
    const [hasError, setHasError] = useState(false);
    const [wasVisible, setWasVisible] = useState(false);
    const [selfIdentifyDisabled, setSelfIdentifyDisabled] = useState(true);
    const [followUpValue, setFollowUpValue] = useState("");
    const [cleanFollowupValue, setCleanFollowupValue] = useState("");
    const [nudgeUpValue, setNudgeUpValue] = useState("");
    const [nudge, setNudge] = useState(null);
    const [prefetchedNudge, setPrefetchedNudge] = useState(null);
    const [prefetchValidChars, setPrefetchValidChars] = useState(0);
    const [prefetchLoading, setPrefetchLoading] = useState(false);
    const [showPrefetch, setShowPrefetch] = useState(false);
    const [showBubble, setShowBubble] = useState(false);
    const [fetchNudge, { isLoading }] = usePost("/api/hire/v1/followup-nudge");
    const { user, hire_id: hireId } = extraData;
    const signature = useMemo(() => {
        if (!extraData.user) {
            return "";
        }
        return `\n-\n${user.full_name}\n${user.email}\n${formatPhone(user.phone)}`;
    }, [extraData?.user]);
    const formatNudgeSubmitValue = (val) => {
        const formatted = `${val.replace(signature, "")}\n\n${nudgeUpValue}`;
        if (selfIdentified) {
            return `${formatted}${signature}`;
        }
        return formatted;
    };
    const setSelfIdentified = (val) => {
        toggleAnonymous(!val);
    };
    const submitValue = nudge ? formatNudgeSubmitValue(followUpValue) : followUpValue;
    const nudged = useSelector((state) => state.nudged);
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.settings);
    const doNudge = () => {
        setShowBubble(true);
        if (prefetchLoading) {
            setShowPrefetch(true);
            return;
        }
        if (prefetchedNudge && prefetchValidChars > 0) {
            // fake a little delay to show the nudge
            setTimeout(() => setNudge(prefetchedNudge), 500);

            if (settings?.GUIDED_FOLLOWUP_FIRST_QUESTION) {
                dispatch(checkinSetNudged(true));
            }
            return;
        }
        if (followUpValue) {
            fetchNudge({
                data: {
                    ...extraData,
                    question_id: id,
                    follow_up_response: cleanFollowupValue,
                    value: selectedAnswerData,
                    response: selectedAnswerData.response,
                },
                onSuccess: (data) => {
                    if (data.nudge) {
                        setNudge(data.nudge);
                        if (settings?.GUIDED_FOLLOWUP_FIRST_QUESTION) {
                            dispatch(checkinSetNudged(true));
                        }
                    }
                },
                onError: () => {
                    // fail invisibly, just go to the next question
                    handleSubmit(submitValue);
                },
            });
        }
    };

    const followUpRef = useRef(null);

    useEffect(() => {
        if (showPrefetch && prefetchedNudge) {
            // user clicked next, we have a nudge to show
            setTimeout(() => setNudge(prefetchedNudge), 500);

            if (settings?.GUIDED_FOLLOWUP_FIRST_QUESTION) {
                dispatch(checkinSetNudged(true));
            }
        }
        if (showPrefetch && !prefetchLoading && !prefetchedNudge) {
            // user clicked next, we don't have a nudge to show
            handleSubmit(submitValue);
        }
    }, [showPrefetch, prefetchedNudge, prefetchLoading]);

    const prefetchNudge = useCallback(
        debounce(
            (answerData) => {
                setPrefetchLoading(true);
                fetchNudge({
                    data: answerData,
                    onSuccess: (data) => {
                        if (data.nudge) {
                            setPrefetchedNudge(data.nudge);
                        }
                        setPrefetchLoading(false);
                    },
                    onError: () => {
                        setPrefetchedNudge(null);
                        setPrefetchLoading(false);
                    },
                });
            },
            500,
            { trailing: true },
        ),
        [fetchNudge, setPrefetchedNudge, setPrefetchLoading],
    );
    const language = useSelector((state) => state.ws.language);
    const brightPromptEnabled = useSelector(
        (state) =>
            !state.company?.settings?.disable_bright_prompt && feature("guided-followup", false),
    );
    const shouldNudge =
        !nudged &&
        brightPromptEnabled &&
        followUpValue.length < FOLLOWUP_MAX_CHARS &&
        followUpValue.length >= FOLLOWUP_MIN_CHARS &&
        wordCountFilter(cleanFollowupValue) &&
        (!settings?.GUIDED_FOLLOWUP_TEST_GROUP || hireId % 2 > 0) &&
        !nudge &&
        language &&
        language.startsWith("en");

    const handleInputChange = (value) => {
        const isError = !value && !optional;
        const val = isError ? "" : value;
        setHasError(isError);
        setFollowUpValue(val);
        setCleanFollowupValue(val);
        setSelfIdentifyDisabled(val === "");
        setPrefetchValidChars((prev) => (prev || 0) - 1);

        if (
            shouldNudge &&
            (!prefetchedNudge || prefetchValidChars <= 0) &&
            val &&
            !prefetchLoading
        ) {
            setPrefetchValidChars(PREFETCH_CHAR_COUNTDOWN);
            prefetchNudge({
                ...extraData,
                question_id: id,
                follow_up_response: cleanFollowupValue,
                value: selectedAnswerData,
                response: selectedAnswerData.response,
            });
        }

        if (handleChange) {
            handleChange(val);
        }
    };

    const onChange = ({ target }) => handleInputChange(target.value);
    const onSubmit = () => {
        if (shouldNudge) {
            doNudge();
        } else if (handleSubmit) {
            handleSubmit(submitValue);
        }
    };

    const onSelfIdentify = ({ target }, checked) => {
        target.blur();

        if (checked) {
            setFollowUpValue((prev) => `${prev}${signature}`);
        } else {
            // if the signature has changed we can't remove it
            setFollowUpValue((prev) => prev.replace(signature, ""));
        }

        setSelfIdentified(checked);
    };

    useEffect(() => {
        if (visible) {
            followUpRef.current?.focus();
            if (wasVisible) {
                handleInputChange(followUpValue);
            } else {
                setWasVisible(true);
            }
        }
    }, [visible]);

    useEffect(() => {
        if (hasError && followUpRef.current) {
            followUpRef.current?.focus();
        }
    }, [hasError]);

    const renderError = () => {
        if (!hasError) {
            return null;
        }

        return <span className="help-block">This field is required.</span>;
    };

    const renderSelfIdentify = () => {
        if (selfIdentifyOff || !user || !(user.full_name || user.email || user.phone) || !hireId) {
            return null;
        }

        return (
            <Checkbox
                checked={selfIdentified}
                onChange={onSelfIdentify}
                disabled={selfIdentifyDisabled}
                data-test="self-identify-checkbox"
            >
                <span
                    className="questions__follow-up-self-identify"
                    /* eslint-disable react/no-danger */
                    dangerouslySetInnerHTML={{
                        __html: _(
                            "Share my identity <strong>for this question only</strong> so I can be contacted",
                        ),
                    }}
                    /* eslint-enable react/no-danger */
                />
            </Checkbox>
        );
    };

    const style = {};
    if (!visible) {
        style.display = "none";
    }

    const placeholder = optional ? _("Optional") : _("Answer...");

    const nextDisabled = !followUpValue.length || (isLoading && !prefetchLoading);
    return (
        <div className="questions__follow-up label-group" style={style}>
            <label htmlFor={`follow_up-${id}`}>{followUpText}</label>
            {showBubble && (
                <div className="questions__follow-up__initial_answer">
                    <TextBubble loaded={!!nudge} text={cleanFollowupValue} />
                </div>
            )}
            {!showBubble && (
                <Textarea
                    disabled={(isLoading && !prefetchLoading) || !!nudge}
                    id={`follow_up-${id}`}
                    name="follow_up"
                    data-test="follow-up-answer-text-area"
                    placeholder={placeholder}
                    onChange={onChange}
                    inputProps={{ maxLength: 5000 }}
                    inputRef={followUpRef}
                />
            )}
            {nudge && (
                <>
                    <Box className="questions__follow-up-nudge">{nudge}</Box>
                    <Textarea
                        id={`nudge_up-${id}`}
                        name="nudge_up"
                        data-test="follow-up-nudge-text-area"
                        placeholder={placeholder}
                        onChange={(e) => setNudgeUpValue(e.target.value)}
                        inputProps={{ maxLength: 5000 }}
                        inputRef={followUpRef}
                    />
                </>
            )}
            {renderSelfIdentify()}
            {renderError()}
            <QuestionButtonGroup
                nextDisabled={nextDisabled}
                handleSkip={handleSkip}
                handleSubmit={onSubmit}
                handleBack={handleBack}
                isLastQuestion={isLastQuestion}
                forceShowSkip={optional}
                loading={isLoading && !prefetchLoading}
            />
        </div>
    );
};

TextFollowUp.propTypes = {
    /* eslint-disable react/require-default-props */
    // These actually are required in the prop types but eslint can't tell
    follow_up: questionProps.follow_up,
    id: questionProps.id,
    /* eslint-enable react/require-default-props */
    selectedAnswerData: PropTypes.shape({ response: PropTypes.string }),
    handleBack: PropTypes.func,
    handleChange: PropTypes.func,
    handleSubmit: PropTypes.func,
    handleSkip: PropTypes.func,
    optional: PropTypes.bool,
    visible: PropTypes.bool,
    isLastQuestion: PropTypes.bool,
    extraData: PropTypes.shape({
        user: userShape,
        hire_id: PropTypes.number,
    }),
    selfIdentifyOff: PropTypes.bool,
    selfIdentified: PropTypes.bool.isRequired,
    toggleAnonymous: PropTypes.func.isRequired,
};

TextFollowUp.defaultProps = {
    selectedAnswerData: null,
    handleBack: null,
    handleChange: null,
    handleSubmit: null,
    handleSkip: null,
    optional: false,
    visible: true,
    isLastQuestion: false,
    extraData: {},
    selfIdentifyOff: false,
};

TextFollowUp.contextTypes = {
    t: PropTypes.func,
};

export default React.memo(TextFollowUp);
